import Vue from 'vue'
import store from '../store/main'
import router from '../router/router'
import Keycloak from 'keycloak-js'
import { parseJwt, base64Encode, base64Decode } from '../utils/sanitizer'
import { clearFkeSummaryCache } from '../utils/fkeSummaryCache'

const initKeys = {
  userInfo: 'userinfo',
  tokenKey: 'okv-token',
  refreshTokenKey: 'okv-refresh-token',
  idTokenKey: 'okv-id-token',
  realm: 'okv-realm',
  clientId: 'okv-client-id',
}

export const defaultRealm = 'fairmat'
const defaultSso = 'https://sso-dev.fairmat.com/sso'

const ambientToKeycloakConfig = [
  { hostname: 'localhost:5001', sso: defaultSso },
  { hostname: 'localhost:5002', sso: 'https://sso-test.fairmat.com/sso' },
  { hostname: 'openkidvita.fairmat.com', sso: 'https://sso.fairmat.com/sso' },
  { hostname: 'openkid-demo.fairmat.com', sso: 'https://sso.fairmat.com/sso' },
  { hostname: 'openkidvita-dev.fairmat.com', sso: defaultSso },
  { hostname: 'openkidvita-test.fairmat.com', sso: 'https://sso-test.fairmat.com/sso' },
]

export const getAccessToken = () => {
  return localStorage.getItem(initKeys.tokenKey) || null
}

const setTokenOnServiceWorker = async (value) => {
  let savedToken = false
  let trySavedToken = 0
  let isInWait = false
  const setTokenOnServiceWorker = async (value) => {
    return new Promise((resolve) => {
      setTimeout(async () => {
        const registrationSW = await navigator.serviceWorker.getRegistration('/release-note-add-token.js')
        if (registrationSW && registrationSW.active) {
          registrationSW.active.postMessage({
            token: value,
            realm: getRealm(),
          })
          resolve(true)
        } else {
          resolve(false)
        }
        resolve()
      }, 1000)
    })
  }

  while (!savedToken && trySavedToken < 10) {
    savedToken = await setTokenOnServiceWorker(value)
    trySavedToken++
  }
}

export const setAccessToken = async (value) => {
  localStorage.setItem(initKeys.tokenKey, value)
  Vue.prototype.$bus.$emit('access-token-updated', value)
  if ('serviceWorker' in navigator) {
    setTokenOnServiceWorker(value)
  }
}

export const isRefreshTokenExpired = () => {
  const refreshTokenData = Vue.$keycloak.refreshTokenParsed
  const refreshTokenTime = new Date(refreshTokenData.exp * 1000).getTime()
  const timeLeft = refreshTokenTime - new Date().getTime()
  return timeLeft <= 0
}

export const getRefreshToken = () => {
  return localStorage.getItem(initKeys.refreshTokenKey) || null
}
export const setRefreshToken = (value) => {
  localStorage.setItem(initKeys.refreshTokenKey, value)
}

export const getIdToken = () => {
  return localStorage.getItem(initKeys.idTokenKey) || null
}
export const setIdToken = (value) => {
  localStorage.setItem(initKeys.idTokenKey, value)
}

export const getUserInfo = () => {
  return localStorage.getItem(initKeys.userInfo)
}
export const setUserInfo = (value) => {
  localStorage.setItem(initKeys.userInfo, value)
}

const getSsoUrl = () => {
  const hostname = getHostname()
  const item = ambientToKeycloakConfig.find((j) => hostname.indexOf(j.hostname) >= 0)
  return item != null ? item.sso : defaultSso
}

const getHostname = () => {
  let port = window.location.port
  let hostname = window.location.hostname
  return port ? hostname + ':' + port : hostname
}

const getSessionId = () => {
  try {
    const userInfo = JSON.parse(getUserInfo())
    return userInfo && userInfo.sessionId ? base64Decode(userInfo.sessionId) : ''
  } catch (e) {
    return ''
  }
}

// Same resolution as fetcher.js: on the default realm the API lives on a dedicated host, on
// tenant realms it is served from the page's own origin.
const getApiBasePath = () => (getRealm() === defaultRealm ? process.env.VUE_APP_API_ENDPOINT : window.location.origin)

export const getRealm = () => {
  let realm = ''
  const hostname = getHostname()

  for (const element of ambientToKeycloakConfig) {
    if (hostname.indexOf(element.hostname) !== -1) {
      realm = hostname.replace(element.hostname, '')
      if (realm.endsWith('-')) {
        realm = realm.slice(0, -1)
      }
      break
    }
  }

  if (realm === undefined || realm === '') {
    return defaultRealm
  }

  return realm
}

export const doLogin = () => {
  // Start every session from a clean FKE-summary cache (covers user switching on a shared machine).
  clearFkeSummaryCache()
  Vue.$keycloak.login({ redirectUri: window.location.href })
}

/**
 * Closes the session on the server. Deliberately does NOT go through the fetcher: a 401 on this
 * call would trigger a renewal and a second logout while the first one is still running.
 * The outcome does not matter — the local cleanup and the Keycloak logout happen either way.
 */
const closeServerSession = () => {
  const accessToken = getAccessToken()
  const sessionId = getSessionId()
  if (!accessToken || !sessionId) {
    return Promise.resolve()
  }

  const realm = getRealm()
  return fetch(getApiBasePath() + '/api/user/logout', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Tenant': realm,
      Authorization: 'Bearer ' + accessToken,
    },
    body: JSON.stringify({ ssoUrl: getSsoUrl(), realm, sessionId }),
  }).catch(() => {})
}

let pendingLogout = null

/**
 * Logs the user out. Several callers can decide to log out in the same moment — typically two
 * concurrent requests whose renewal failed — so concurrent calls collapse into one: the session
 * is closed once, not once per caller.
 */
export const doLogout = () => {
  if (pendingLogout) {
    return pendingLogout
  }

  // keycloak-js drops its in-memory tokens when a renewal fails, and the logout URL carries an
  // id_token_hint only when an id token is there. Without it Keycloak cannot end the session
  // silently and asks the user to confirm, so an ordinary expiry looks like a malfunction.
  const idTokenForLogout = getIdToken()

  pendingLogout = closeServerSession()
    .then(() => {
      removeAccessData()
      clearFkeSummaryCache()
      if (!Vue.$keycloak.idToken && idTokenForLogout) {
        Vue.$keycloak.idToken = idTokenForLogout
      }
      Vue.$keycloak.logout()
    })
    .finally(() => {
      pendingLogout = null
    })

  return pendingLogout
}

export const resetPassword = () => {
  removeSessionData()
  Vue.$keycloak.login({ action: 'UPDATE_PASSWORD' })
}

export const removeSessionData = () => {
  removeAccessData()
  store.dispatch('scoringProfilesCommon/clearAllStoreModules')
}

export const handleError = (e) => {
  return router.push({ name: 'http-error', params: { code: e } })
}

export const parseToken = (value, ctx) => {
  const info = parseJwt(value)
  if (info == null) return null

  let result = {
    roles: [],
    email: info.email,
    username: info.preferred_username,
    sessionId: info.sid ? base64Encode(info.sid) : '',
  }

  // Keycloak omits realm_access entirely when the user has no realm roles.
  const roles = info.realm_access ? info.realm_access.roles : []
  if (Array.isArray(roles) && roles.length > 0) {
    if (ctx != null) {
      roles.forEach((r) => {
        if (r.includes(ctx)) {
          const role = r.split(ctx)[1]
          const int = Number(role)
          !isNaN(int) ? result.roles.push(int) : null
        }
      })
    } else {
      result.roles = [...roles]
    }
  }

  return result
}

let pendingRefresh = null

const issuedAt = (token) => {
  const parsed = token ? parseJwt(token) : null
  return parsed && typeof parsed.iat === 'number' ? parsed.iat : null
}

const isTokenExpired = (token, minValidityInSeconds = 5) => {
  const parsed = token ? parseJwt(token) : null
  if (!parsed || typeof parsed.exp !== 'number') {
    return true
  }
  return parsed.exp - minValidityInSeconds <= Math.floor(Date.now() / 1000)
}

/**
 * Copies the tokens found in localStorage into the Keycloak instance held in memory.
 *
 * Every tab runs its own Keycloak instance but they all share localStorage, so a tab that has been
 * sitting idle can hold credentials older than the ones another tab has just obtained. Renewing
 * with those is refused by Keycloak — the refresh token belongs to a client session that a later
 * login has already replaced — and the tab is logged out although the session is perfectly alive.
 *
 * Returns true when newer tokens were adopted.
 */
const adoptStoredTokens = () => {
  const storedAccess = getAccessToken()
  const storedRefresh = getRefreshToken()
  if (!storedAccess || !storedRefresh || storedRefresh === Vue.$keycloak.refreshToken) {
    return false
  }

  const storedIssuedAt = issuedAt(storedRefresh)
  const memoryIssuedAt = issuedAt(Vue.$keycloak.refreshToken)
  // Only move forward: an unreadable or older token is left alone.
  if (storedIssuedAt === null || (memoryIssuedAt !== null && storedIssuedAt <= memoryIssuedAt)) {
    return false
  }

  Vue.$keycloak.token = storedAccess
  Vue.$keycloak.tokenParsed = parseJwt(storedAccess)
  Vue.$keycloak.refreshToken = storedRefresh
  Vue.$keycloak.refreshTokenParsed = parseJwt(storedRefresh)

  const storedId = getIdToken()
  if (storedId) {
    Vue.$keycloak.idToken = storedId
    Vue.$keycloak.idTokenParsed = parseJwt(storedId)
  }

  return true
}

// A renewal in another tab reaches this one as a storage event. Adopting it right away keeps the
// in-memory instance aligned, so this tab never sends a request with a token it already knows is
// superseded.
if (typeof window !== 'undefined' && window.addEventListener) {
  window.addEventListener('storage', (event) => {
    if (event && event.key !== null && event.key !== initKeys.tokenKey && event.key !== initKeys.refreshTokenKey) {
      return
    }
    if (adoptStoredTokens()) {
      Vue.prototype.$bus.$emit('access-token-updated', Vue.$keycloak.token)
    }
  })
}

/**
 * Renews the access token when needed.
 *
 * Resolves with `true` when the token actually changed, with `false` when it was still valid and
 * there was nothing to renew — a normal outcome, not an error. Rejects only when the renewal
 * genuinely fails (dead refresh token): that is the only case where the session is over.
 *
 * A single renewal is in flight at any time: concurrent 401s share one outcome instead of
 * renewing in turn and invalidating each other.
 */
export const refreshToken = () => {
  if (pendingRefresh) {
    return pendingRefresh
  }

  const tokenBefore = Vue.$keycloak.token

  // Another tab may already have done the work: adopt what it obtained before asking Keycloak.
  const adopted = adoptStoredTokens()
  if (adopted && !isTokenExpired(Vue.$keycloak.token)) {
    return Promise.resolve(Vue.$keycloak.token !== tokenBefore)
  }

  pendingRefresh = Vue.$keycloak
    .updateToken()
    .then(() => {
      setAccessToken(Vue.$keycloak.token)
      setRefreshToken(Vue.$keycloak.refreshToken)
      setIdToken(Vue.$keycloak.idToken)
      return Vue.$keycloak.token !== tokenBefore
    })
    .finally(() => {
      pendingRefresh = null
    })

  return pendingRefresh
}

export const getClientId = () => {
  const key = localStorage.hasOwnProperty(initKeys.clientId)
  return key ? localStorage.getItem(initKeys.clientId) : 'okv-general-client'
}

export const setClientId = (value) => {
  localStorage.setItem(initKeys.clientId, value)
}

export const removeAccessData = () => {
  localStorage.removeItem(initKeys.userInfo)
  localStorage.removeItem(initKeys.tokenKey)
  localStorage.removeItem(initKeys.refreshTokenKey)
  localStorage.removeItem(initKeys.realm)
  localStorage.removeItem(initKeys.clientId)
  localStorage.removeItem(initKeys.idTokenKey)
}

export const initOptions = {
  url: getSsoUrl(),
  realm: getRealm(),
  clientId: getClientId(),
  token: getAccessToken(),
  refreshToken: getRefreshToken(),
  idToken: getIdToken(),
  onLoad: 'login-required',
  checkLoginIframe: false,
}

const _keycloak = new Keycloak(initOptions)

const Plugin = {
  install(Vue) {
    Vue.$keycloak = _keycloak
  },
}

Plugin.install = (Vue) => {
  Vue.$keycloak = _keycloak
  Object.defineProperties(Vue.prototype, {
    $keycloak: {
      get() {
        return _keycloak
      },
    },
  })
}

Vue.use(Plugin)

export default Plugin
